import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getTranslation } from "../../translations/getTranslations";
import { useNotification } from "../../notifications/Notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { setFullName } from "../../redux/slices/authSlice";
import { updateFullName } from "../../services/userService";
import DeleteUserModal from "../modals/DeleteUserModal";
import ChangeMailModal from "../modals/ChangeMailModal";
import ChangePasswordModal from "../modals/ChangePasswordModal";

const LoginInfoForm = ({ isMobile }) => {
  const currentFullName = useSelector(state => state.auth.user.fullName);
  const [errorMessage, setErrorMessage] = useState("");
  const [newFullName, setNewFullName] = useState(currentFullName);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangeMailModalOpen, setIsChangeMailModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const language = useSelector(state => state.settings.language);

  const notify = useNotification();
  const dispatch = useDispatch();

  const translation = key => getTranslation("loginInfoForm", key, language);

  useEffect(() => {
    setErrorMessage("");
  }, []);

  const handleFullNameChange = async () => {
    if (!newFullName) {
      setErrorMessage(translation("_fullNameRequired"));
      return;
    }

    await updateFullName(newFullName);

    dispatch(setFullName(newFullName));
    notify("success", translation("_changeFullNameSuccess"));
    setIsEditing(false);
    setErrorMessage("");
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setErrorMessage("");
  };

  const handleCancelEdit = () => {
    setNewFullName(currentFullName);
    setIsEditing(false);
    setErrorMessage("");
  };

  return (
    <div className={`flex h-full flex-col justify-center text-lg`}>
      <div className="mb-2 flex items-center text-2xl font-semibold">
        <FontAwesomeIcon icon={faKey} className="mr-2" /> {translation("_loginInfo")}
      </div>
      <p>{translation("_loginDescription")}</p>
      <div className={`flex h-full w-full items-center justify-center text-lg`}>
        <div className={`mb-2 ${isMobile ? "w-full px-2" : "w-[40%]"}`}>
          <label>{translation("_fullName")}</label>
          <div className="relative flex items-center">
            <input
              type="text"
              className={`w-full rounded border border-inputBorder bg-inputBackground p-2 text-textPrimary hover:bg-inputHover focus:outline-none`}
              value={newFullName}
              onChange={e => setNewFullName(e.target.value)}
              disabled={!isEditing}
            />

            {isEditing ? (
              <div className="absolute right-0 flex items-center">
                <FontAwesomeIcon icon={faSave} className="mr-2 cursor-pointer" onClick={handleFullNameChange} />
                <FontAwesomeIcon
                  icon={faTimes}
                  className={`mr-2 cursor-pointer text-buttonCancel`}
                  onClick={handleCancelEdit}
                />
              </div>
            ) : (
              <div className="absolute right-0 flex items-center">
                <FontAwesomeIcon icon={faEdit} className="mr-2 cursor-pointer" onClick={() => setIsEditing(true)} />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Error Message */}
      <p className="mt-2 h-10 text-sm text-red-500">{errorMessage}</p>
      {/* Button Section */}
      <div className="mt-4 flex justify-center">
        <div className={`flex gap-2 ${isMobile ? "flex-col items-center" : "w-full justify-between lg:w-[70%]"}`}>
          <button
            className={`h-[40px] w-[160px] rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover`}
            onClick={() => setIsChangePasswordModalOpen(true)}
          >
            {translation("_changePassword")}
          </button>
          <button
            className={`h-[40px] w-[160px] rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover`}
            onClick={() => {
              setIsChangeMailModalOpen(true);
            }}
          >
            {translation("_changeEmail")}
          </button>
          <button
            className={`h-[40px] w-[160px] rounded bg-buttonCancel p-1 text-buttonText hover:bg-buttonCancelHover`}
            onClick={handleOpenDeleteModal}
          >
            {translation("_deleteUser")}
          </button>
        </div>
      </div>
      <DeleteUserModal
        showModal={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        currentUsername={currentFullName}
      />
      <ChangePasswordModal showModal={isChangePasswordModalOpen} onClose={() => setIsChangePasswordModalOpen(false)} />
      <ChangeMailModal showModal={isChangeMailModalOpen} onClose={() => setIsChangeMailModalOpen(false)} />
    </div>
  );
};

export default LoginInfoForm;
